import { reduce } from 'lodash';
import { OrderTypes, TicketStatuses } from '@sda/enums';
import { PrivateTicketMask, PublicTicketMask } from '@sda/sdk/masks';
import { Reducers } from '@sda/enums/store';
import { SDKFactory } from './helpers/sdk.factory';
import { SDK } from './helpers/sdk';
import type { FetchDataType, FirestoreClientData } from './interfaces';
import type { PrivateTicket, PublicTicket, Ticket } from '@sda/interfaces/ticket';
import type { Order, ReconstructedTicketOrderResults } from '@sda/interfaces/order';

/**
 * SDK Class to interact with Firestore Shows.
 */
export class Tickets extends SDK<PublicTicket> {
  constructor(apiClient: FirestoreClientData) {
    super(apiClient, Reducers.TICKETS);

    this.collection = this.fsh.Schema.PublicTickets;

    this.PublicMap = PublicTicketMask;

    this.PrivateMap = PrivateTicketMask;
  }

  async getCombined(id: string): Promise<Ticket & FetchDataType> {
    return this.getCombinedRecord<PrivateTicket>(
      id,
      this.Private().collection,
    );
  }

  Private(): SDK<PrivateTicket> {
    return SDKFactory<PrivateTicket>(this.fsh.clientData, this.fsh.Schema.PrivateTickets, { reducerName: Reducers.TICKETS });
  }

  /**
   * Given an object containing merged public and private tickets, generate a map of orders
   * @param  {object} tickets
   * @param  {number} ticketPrice
   * @return {object}
   */
  reconstructOrdersFromTicketsV2 = (tickets: (Ticket & FetchDataType)[], orders: Order[]): ReconstructedTicketOrderResults => reduce(
    orders,
    (results, order) => ({
      ...results,
      [order.paymentIntentId]: {
        id: order.paymentIntentId,
        amount: order.metadata.gross,
        amount_returned: 0,
        status: order.status,
        quantity: order.products[0].quantity,
        containsDoorSale: order.orderType === OrderTypes.ORDER_TYPE_DOOR,
        taxTotal: order.metadata.taxTotal,
        net: order.metadata.netTotal,
        stripeFee: order.metadata.stripeFee,
        taxRate: order.products[0].taxRate,
        unitPrice: order.products[0].unitPrice,
        metadata: {
          orderType: order.orderType,
          showId: order.showId,
        },
        tickets: tickets.filter((t) => order.products[0].tickets.indexOf(t.id) !== -1),
      },
    }),
    {},
  );

  reconstructOrdersFromTickets = (
    tickets: Ticket[],
    ticketPrice: number,
  ): ReconstructedTicketOrderResults => reduce<Ticket, any>(
    tickets,
    (results: ReconstructedTicketOrderResults, ticket) => {
      if (ticket.status === TicketStatuses.COMPED) {
        if (!results[ticket.purchaserEmail]) {
          return {
            ...results,
            [ticket.purchaserEmail]: {
              id: ticket.purchaserEmail,
              amount: 0,
              amount_returned: 0,
              status: TicketStatuses.COMPED,
              quantity: 1,
              containsDoorSale: false,
              metadata: {
                orderType: OrderTypes.ORDER_TYPE_COMP,
                showId: ticket.showId,
              },
              tickets: [
                ticket,
              ],
            },
          };
        }

        return {
          ...results,
          [ticket.purchaserEmail]: {
            ...results[ticket.purchaserEmail],
            tickets: [
              ...results[ticket.purchaserEmail].tickets,
              ticket,
            ],
          },
        };
      }

      if (!results[ticket.stripeOrderId]) {
        return {
          ...results,
          [ticket.stripeOrderId]: {
            id: ticket.stripeOrderId,
            amount: ticketPrice,
            amount_returned: 0,
            status: ticket.status === TicketStatuses.PURCHASED || ticket.status === TicketStatuses.PASS ? 'fulfilled' : 'returned',
            quantity: 1,
            containsDoorSale: ticket.stripeOrderType === OrderTypes.ORDER_TYPE_DOOR,
            metadata: {
              orderType: ticket.stripeOrderType,
              showId: ticket.showId,
            },
            tickets: [
              ticket,
            ],
          },
        };
      }

      return {
        ...results,
        [ticket.stripeOrderId]: {
          ...results[ticket.stripeOrderId],
          amount: results[ticket.stripeOrderId].amount + ticketPrice,
          quantity: results[ticket.stripeOrderId].quantity + 1,
          containsDoorSale: ticket.stripeOrderType === OrderTypes.ORDER_TYPE_DOOR || results[ticket.stripeOrderId].containsDoorSale,
          tickets: [
            ...results[ticket.stripeOrderId].tickets,
            ticket,
          ],
        },
      };
    },
    {},
  );
}
